import { inject, Injectable, Injector } from '@angular/core';
import { ConfigurationService, EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { AUTH_CONFIG_IDS, AuthConfigId } from '../../auth/auth.config';
import { SessionStore } from '../../core/services/session.store';

import { NavigationService } from './NavigationService';

@Injectable()
export class SecurityService {
    private readonly injector = inject(Injector);

    constructor(
        private oidcSecurityService: OidcSecurityService,
        private configurationService: ConfigurationService,
        private navigationService: NavigationService,
        private eventsSource: PublicEventsService,
    ) {}

    isAuthenticated(configId: AuthConfigId): Observable<boolean> {
        return this.eventsSource.registerForEvents().pipe(
            filter(
                (event) =>
                    event.type === EventTypes.CheckingAuthFinished ||
                    event.type === EventTypes.CheckingAuthFinishedWithError,
            ),
            switchMap(() => this.oidcSecurityService.isAuthenticated(configId)),
            take(1),
        );
    }

    async login(configId: AuthConfigId): Promise<void> {
        const loginRedirectUrl = location.href.replace('/unauthorized', '');
        const encodedLoginRedirectUrl = btoa(loginRedirectUrl);

        const activeConfigId = configId === AUTH_CONFIG_IDS.ADMIN ? null : await this.getCurrentSession();

        await firstValueFrom(this.oidcSecurityService.setState(encodedLoginRedirectUrl, configId));

        if (activeConfigId && activeConfigId !== configId) {
            this.logout(activeConfigId, loginRedirectUrl);
            return;
        }

        this.oidcSecurityService.authorize(configId);
    }

    logout(configId: AuthConfigId, redirectUrl?: string): void {
        combineLatest([
            this.configurationService.getOpenIDConfiguration(configId),
            this.oidcSecurityService.getIdToken(configId),
        ])
            .pipe(take(1))
            .subscribe(([authConfig, idToken]) => {
                if (!authConfig?.authWellknownEndpoints) {
                    console.error(`Cannot log out of "${configId}": its OpenID configuration is not loaded.`);
                    return;
                }

                const logoutUrl = new URL(authConfig.authWellknownEndpoints.endSessionEndpoint ?? '');
                logoutUrl.searchParams.set('client_id', authConfig.clientId ?? '');
                logoutUrl.searchParams.set(
                    'post_logout_redirect_uri',
                    redirectUrl ?? authConfig.postLogoutRedirectUri ?? '',
                );
                if (idToken) {
                    logoutUrl.searchParams.set('id_token_hint', idToken);
                }

                // Resolved here: SessionStore → UserService → HttpService → SecurityService is a cycle.
                this.injector.get(SessionStore).clear();
                this.oidcSecurityService.logoffLocal(configId);
                this.navigationService.goTo(logoutUrl.toString());
            });
    }

    getAuthConfigId(pathname = window.location.pathname): AuthConfigId {
        if (pathname.startsWith('/admin')) {
            return AUTH_CONFIG_IDS.ADMIN;
        }
        if (pathname.startsWith('/enduser') || pathname.startsWith('/query')) {
            return AUTH_CONFIG_IDS.OWNER;
        }
        return AUTH_CONFIG_IDS.INSTALLER;
    }

    async getCurrentSession(): Promise<AuthConfigId | null> {
        for (const configId of [AUTH_CONFIG_IDS.INSTALLER, AUTH_CONFIG_IDS.OWNER]) {
            const isAuthenticated = await firstValueFrom(this.oidcSecurityService.isAuthenticated(configId));

            if (isAuthenticated) {
                return configId;
            }
        }

        return null;
    }
}
